import { Alert, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useBoolean, useEffectOnce } from 'react-use'

import PageSection from 'components/common/PageSection'
import {
  AWSAuthenticationType,
  AWSRoleCreationMethod,
  isIamRoleAuthUnavailable
} from 'components/status-checks/authentication-form-utils'
import { AWSAuthenticationAccountSelect } from 'components/status-checks/form/authentication/aws/AWSAuthenticationAccountSelect'
import { AWSAuthenticationTypeSelect } from 'components/status-checks/form/authentication/aws/AWSAuthenticationTypeSelect'
import { AWSIAMRoleSelect } from 'components/status-checks/form/authentication/aws/AWSIAMRoleSelect'
import { AWSServiceAccountAuthentication } from 'components/status-checks/form/authentication/aws/AWSServiceAccountAuthentication'
import { ManagedPolicies } from 'components/status-checks/form/authentication/aws/steps/ManagedPolicies'
import { PermissionPolicy } from 'components/status-checks/form/authentication/aws/steps/PermissionPolicy'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { type AWSIAMRoleMetadata } from 'types/api/aws/metadata'
import { TeamExternalIntegrationAuthenticationStatus } from 'types/api/status-checks/status-check'
import { isPrivateEdition } from 'utils/app-config'
import { messageFromResponseError } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

const AWSIAMRoleAuthentication: React.FC<{
  awsIAMRoleMetadata?: AWSIAMRoleMetadata
  awsRoleCreationMethod?: AWSRoleCreationMethod
}> = ({ awsIAMRoleMetadata, awsRoleCreationMethod }) => {
  if (isIamRoleAuthUnavailable(awsIAMRoleMetadata)) {
    return (
      <Alert severity="warning">
        This feature currently isn't enabled. Contact your administrator for
        support. Reason: A proxy ARN must be configured to fetch AWS data. Check
        that the GREMLIN_PROXY_ROLE_ARN environment variable was set.
      </Alert>
    )
  }

  if (!awsRoleCreationMethod) {
    return null
  }

  return <AWSIAMRoleSelect awsRoleCreationMethod={awsRoleCreationMethod} />
}

export const ObservabilityToolAWSAuthenticationDetails = () => {
  const [isCreatingNewAccount, setIsCreatingNewAccount] = useBoolean(false)
  const { getAvailableExternalIntegrations } =
    useStatusCheckExternalIntegrationsContext()
  const {
    isCreatingAuthenticationOnly,
    isFetchingAwsIAMRoleMetadata,
    hasErrorAwsIAMRoleMetadata,
    awsIAMRoleMetadataError,
    awsIAMRoleMetadata,
    isEdit,
    isOnboarding
  } = useObservabilityToolAuthenticationContext()

  const { control, setValue } = useFormContext<StatusCheckFormValues>()

  const [
    awsAuthenticationType,
    awsRoleCreationMethod,
    selectedObservabilityTool,
    externalIntegration
  ] = useWatch({
    control,
    name: [
      'awsAuthenticationType',
      'awsRoleCreationMethod',
      'observabilityTool',
      'externalIntegration'
    ]
  })

  const externalIntegrations = getAvailableExternalIntegrations(false)

  const hasAuthenticatedAWSAccounts = useMemo(() => {
    return externalIntegrations.some(
      (externalIntegration) =>
        externalIntegration.type === IntegrationType.AWS &&
        externalIntegration.lastAuthenticationStatus ===
          TeamExternalIntegrationAuthenticationStatus.Authenticated
    )
  }, [externalIntegrations])

  const domains = selectedObservabilityTool?.domains

  const shouldShowAWSAuthenticationAccountSelect = useMemo(() => {
    if (isEdit || isCreatingAuthenticationOnly) {
      return false
    }

    if (hasAuthenticatedAWSAccounts) {
      return true
    }

    return Boolean(domains?.length)
  }, [
    domains?.length,
    hasAuthenticatedAWSAccounts,
    isCreatingAuthenticationOnly,
    isEdit
  ])

  // If the conditions are met to create a new account, set isCreatingNewAccount to true
  useEffect(() => {
    if (!isCreatingNewAccount && !isEdit && domains?.length === 0) {
      setIsCreatingNewAccount(true)
    }
  }, [domains?.length, isCreatingNewAccount, isEdit, setIsCreatingNewAccount])

  // Private Edition only supports the Manual IAM-role creation flow (no CloudFormation),
  // so coerce the role-creation method to Manual whenever IAM Role is active in PE.
  useEffect(() => {
    if (
      isPrivateEdition() &&
      awsAuthenticationType === AWSAuthenticationType.IAMRole &&
      awsRoleCreationMethod !== AWSRoleCreationMethod.Manual
    ) {
      setValue('awsRoleCreationMethod', AWSRoleCreationMethod.Manual)
    }
  }, [awsAuthenticationType, awsRoleCreationMethod, setValue])

  useEffectOnce(() => {
    if (!isEdit) return

    if (externalIntegration?.integrationSpecificValues?.AWS_IAM_ROLE_ARN) {
      setValue('awsAuthenticationType', AWSAuthenticationType.IAMRole)
      return
    }

    setValue('awsAuthenticationType', AWSAuthenticationType.ServiceAccount)
  })

  return (
    <PageSection
      errorMessage={messageFromResponseError(
        awsIAMRoleMetadataError ?? null,
        'There was an error retrieving the AWS IAM Role metadata. Please try again.'
      )}
      hasError={hasErrorAwsIAMRoleMetadata}
      isLoading={isFetchingAwsIAMRoleMetadata}
    >
      <Stack spacing={2}>
        <Stack spacing={2}>
          {!isEdit && !isOnboarding && (
            <Typography mt={2} variant="h6">
              Authentication
            </Typography>
          )}
          {shouldShowAWSAuthenticationAccountSelect && (
            <AWSAuthenticationAccountSelect
              domains={domains ?? []}
              isCreatingNewAccount={isCreatingNewAccount}
              setIsCreatingNewAccount={setIsCreatingNewAccount}
            />
          )}
        </Stack>
        {(isCreatingNewAccount || isEdit || isCreatingAuthenticationOnly) && (
          <Stack spacing={2}>
            <AWSAuthenticationTypeSelect />
            {awsAuthenticationType === AWSAuthenticationType.IAMRole && (
              <AWSIAMRoleAuthentication
                awsIAMRoleMetadata={awsIAMRoleMetadata}
                awsRoleCreationMethod={awsRoleCreationMethod}
              />
            )}
            {awsAuthenticationType === AWSAuthenticationType.ServiceAccount && (
              <>
                <AWSServiceAccountAuthentication />
                <Stack sx={{ pl: 1 }}>
                  <PermissionPolicy badeCount={1} />
                  <ManagedPolicies badgeCount={2} />
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </PageSection>
  )
}
